// CLDR plural rules polyfill — happy-dom / Node do not ship Arabic CLDR
// data by default, so Intl.PluralRules("ar").select(3) falls back to
// English's 2-form categorization. The @formatjs polyfill patches
// Intl.PluralRules and the /locale-data/* side-effect imports register
// CLDR data for each locale we support. Must be imported BEFORE i18next-icu.
import "@formatjs/intl-pluralrules/polyfill";
import "@formatjs/intl-pluralrules/locale-data/en";
import "@formatjs/intl-pluralrules/locale-data/ar";

import i18next, { type i18n as I18nInstance } from "i18next";
import ICU, { type IcuConfig, type IcuInstance } from "i18next-icu";
import HttpBackend from "i18next-http-backend";
import LanguageDetector from "i18next-browser-languagedetector";
import { initReactI18next } from "react-i18next";

import { DEFAULT_LOCALE, EAGER_NAMESPACES, SUPPORTED_LOCALES, type Locale } from "./namespaces";
import { attachDirectionDriver } from "./direction";
import { missingKeyHandler } from "./missingKeyHandler";
import { createIcuParseErrorHandler, type IntlMessageFormatCtor } from "./icuParseErrorHandler";
import { intlLocale } from "./intlLocale";
import { formatCount } from "./formatCount";

/**
 * Which locale ICU (intl-messageformat) formats a message with.
 *
 * This locale shapes the plural `#` and typed `{count, number}` arguments. It
 * does NOT reach a plain `{count}` argument: intl-messageformat renders that
 * with `String(value)`, so it would print Latin digits in Arabic.
 * `localizePlainNumbers` below covers those.
 *
 * i18next's resolved language is the bare `"ar"`, and `Intl` renders bare `"ar"`
 * with WESTERN digits, so map the UI language to the canonical tag every other
 * formatter uses (`intlLocale` → `"ar-JO"`, Arabic-Indic by default, honouring the
 * per-user numerals preference). English is passed through unchanged.
 */
function parseLngForICU(lng: string): string {
  return lng.startsWith("ar") ? intlLocale(lng) : lng;
}

/**
 * What ICU skips around an argument name (Pattern_White_Space). Unlike JS `\s`
 * it includes U+0085 and the U+200E/U+200F direction marks, and excludes NBSP.
 */
const ICU_WHITE_SPACE = /[\t-\r \u0085\u200E\u200F\u2028\u2029]/g;

/**
 * Format the numbers that a message prints as plain `{x}` arguments, the same
 * way `formatCount` does (Arabic-Indic in Arabic unless the user chose Latin
 * numerals, Latin in English, default grouping).
 *
 * A name the message also uses as `{x, plural|select|number|date|time …}` keeps
 * its raw number, so plural/select choice and typed formatting still work; its
 * plain use then stays Latin. A value the message doesn't print is left alone.
 * Only real numbers are touched: a numeric-looking string (an employee code, an
 * IBAN, a phone number) passes through as written. A year must be passed
 * ungrouped by the caller (`formatNumber(year, undefined, { useGrouping: false })`),
 * or it would read "2,026".
 */
function localizePlainNumbers(
  res: unknown,
  options: Record<string, unknown> | undefined,
  lng: string,
): Record<string, unknown> | undefined {
  if (typeof res !== "string" || !options) return options;
  let compact: string | undefined;
  let out: Record<string, unknown> | undefined;
  for (const [name, value] of Object.entries(options)) {
    if (typeof value !== "number" || !Number.isFinite(value)) continue;
    compact ??= res.replace(ICU_WHITE_SPACE, "");
    if (!compact.includes(`{${name}}`) || compact.includes(`{${name},`)) continue;
    out ??= { ...options };
    out[name] = formatCount(value, lng);
  }
  return out ?? options;
}

/** i18next-icu's runtime `parse` (called by i18next for every translation), untyped upstream. */
interface IcuWithParse extends IcuInstance {
  parse(
    res: string,
    options: Record<string, unknown> | undefined,
    lng: string,
    ...rest: unknown[]
  ): string;
}

/** i18next-icu, with numbers in plain `{x}` arguments shaped for the UI language. */
class LocalizedIcu extends (ICU as unknown as new (config: IcuConfig) => IcuWithParse) {
  override parse(
    res: string,
    options: Record<string, unknown> | undefined,
    lng: string,
    ...rest: unknown[]
  ): string {
    return super.parse(res, localizePlainNumbers(res, options, lng), lng, ...rest);
  }
}

/**
 * Eager resource bundles — statically imported so they land in the initial
 * JS chunk. Every key in these namespaces is resolvable synchronously after
 * `initI18n()` resolves (no Suspense fallback on first render).
 *
 * Per-feature namespaces (employees, leaves, attendance, …) lazy-load via
 * `i18next-http-backend` from `/locales/{{lng}}/{{ns}}.json`. Apps serve
 * those bundles from their `public/locales/` directory.
 */
import enCommon from "./locales/en/common.json";
import enErrors from "./locales/en/errors.json";
import enValidation from "./locales/en/validation.json";
import enComingSoon from "./locales/en/coming-soon.json";
import arCommon from "./locales/ar/common.json";
import arErrors from "./locales/ar/errors.json";
import arValidation from "./locales/ar/validation.json";
import arComingSoon from "./locales/ar/coming-soon.json";

const eagerResources = {
  en: {
    common: enCommon,
    errors: enErrors,
    validation: enValidation,
    "coming-soon": enComingSoon,
  },
  ar: {
    common: arCommon,
    errors: arErrors,
    validation: arValidation,
    "coming-soon": arComingSoon,
  },
} as const;

export interface InitI18nOptions {
  /**
   * Optional locale override. Useful in tests and in SSR scenarios where the
   * caller already resolved the locale from a cookie on the server. When
   * unset, the language detector resolves from:
   *   1. `linkhr_locale` cookie
   *   2. `linkhr:locale` localStorage key
   *   3. `navigator.language` (Accept-Language equivalent in browsers)
   *   4. `<html lang>` attribute
   *   → falls back to DEFAULT_LOCALE ("en")
   */
  locale?: Locale;
  /**
   * Base URL pattern for lazy namespace fetches (i18next-http-backend).
   * Defaults to `/locales/{{lng}}/{{ns}}.json` — apps serve these from
   * `public/locales/`.
   */
  backendLoadPath?: string;
}

/**
 * Initialize the shared i18next instance.
 *
 * Call once per app, BEFORE `createRoot(...).render(...)`. The order matters:
 *   1. `initI18n()` resolves locale + attaches the direction driver, so
 *      `<html dir>` / body font class are correct on first paint.
 *   2. React mounts and `useTranslation()` reads the already-loaded bundles.
 *
 * Closes Pitfalls:
 *   - **C6** via `missingKeyHandler` + `saveMissing: true` (throws in dev,
 *     Sentry-logs in prod, never renders the key string).
 *   - **C7** via `i18next-icu` + Arabic bundles that express all 6 CLDR
 *     plural forms (zero, one, two, few, many, other). See `plurals.test.ts`.
 */
export async function initI18n(opts: InitI18nOptions = {}): Promise<I18nInstance> {
  await i18next
    // Register ICU with (a) parseLngForICU so Arabic plurals and typed numbers
    // render in the canonical Intl tag (Arabic-Indic, honouring the numerals
    // preference), with LocalizedIcu doing the same for plain `{x}` numbers,
    // and (b) a parse-error guard: if a value has ICU placeholders but the call
    // site passed no matching args, intl-messageformat throws and the default
    // handler would return the RAW template to the user. The guard warns once
    // per key, re-runs a lenient format when the caller passed a partial set of
    // args (keeping the resolved parts), and otherwise strips the placeholder
    // blocks — so a raw `{x, plural, …}` string can never render and a message
    // never blanks. `IntlMessageFormat` is assigned onto the i18next instance by
    // i18next-icu at init, so the getter resolves it lazily.
    .use(
      new LocalizedIcu({
        parseLngForICU,
        parseErrorHandler: createIcuParseErrorHandler({
          getIntlMessageFormat: () =>
            (i18next as unknown as { IntlMessageFormat?: IntlMessageFormatCtor }).IntlMessageFormat,
          getLocale: () => i18next.language,
        }),
      }),
    )
    .use(HttpBackend)
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      resources: eagerResources,
      fallbackLng: DEFAULT_LOCALE,
      supportedLngs: [...SUPPORTED_LOCALES],
      nonExplicitSupportedLngs: true,
      // The detector can hand us a region tag ("en-US", "ar-JO"). Bundles exist
      // per language only, so fetching `/locales/en-US/…` first was a wasted
      // request the SPA host answered with its HTML page.
      load: "languageOnly",

      // I18N-02: eager namespaces bundled; others lazy via HttpBackend.
      ns: [...EAGER_NAMESPACES],
      defaultNS: "common",

      // I18N-01 — explicitly register ICU as the post-processor. i18next v26
      // + i18next-icu v2.3 do NOT auto-wire the post-processor pipeline when
      // chained via .use(ICU) alone. Without this, t('items_count', { count: 1 })
      // returns the raw "{count, plural, ...}" template string and plain
      // "{field}" interpolation also fails. See 00-VERIFICATION.md Gap 1.
      postProcess: ["icu"],

      partialBundledLanguages: true,

      // If the caller pinned a locale (e.g. tests), respect it. Otherwise
      // the LanguageDetector chain below resolves it.
      ...(opts.locale ? { lng: opts.locale } : {}),

      // Pitfall C6 / I18N-08 — missing keys.
      saveMissing: true,
      missingKeyHandler,

      // I18N-07 — numeric interpolation is handled by `formatNumber()`; we
      // don't escape because React already escapes renderables.
      interpolation: { escapeValue: false },

      // I18N-06 — locale resolution order.
      detection: {
        order: ["cookie", "localStorage", "navigator", "htmlTag"],
        caches: ["cookie", "localStorage"],
        lookupCookie: "linkhr_locale",
        lookupLocalStorage: "linkhr:locale",
        cookieMinutes: 60 * 24 * 365, // 1 year
        cookieOptions: {
          path: "/",
          sameSite: "strict",
          secure:
            typeof globalThis.location !== "undefined" && globalThis.location.protocol === "https:",
        },
      },

      // Lazy namespaces — per-feature bundles served by the app host.
      backend: {
        loadPath: opts.backendLoadPath ?? "/locales/{{lng}}/{{ns}}.json",
      },

      // `numeralsChanged` is a dedicated event `setNumeralsMode` emits so every
      // useTranslation consumer re-renders when the user flips Arabic-Indic ↔
      // Latin digits. It is deliberately NOT `languageChanged` (that one wipes
      // the TanStack Query cache — see hr-admin main.tsx). Default binding is
      // just `languageChanged`, so both must be listed here to keep language
      // switches working.
      react: { useSuspense: true, bindI18n: "languageChanged numeralsChanged" },
    });

  attachDirectionDriver(i18next);
  return i18next;
}

export { i18next };

/**
 * Format a number for the given UI language, honouring the per-user numerals
 * preference: Arabic-Indic (٠-٩) by default in Arabic, Latin (0-9) when the user
 * toggles Latin numerals; English is always Latin. Routes through `intlLocale`
 * — the same numerals-aware path as counts, money, and dates — so a bare number
 * never renders a different digit shape than the copy around it.
 *
 * (Supersedes the former I18N-07 "always Western" behaviour, reversed by the
 * founder-decided Arabic-Indic policy.)
 *
 * `options` passes through to `Intl.NumberFormat` — e.g. fixed fraction digits
 * to show a server's exact decimal as written (5500.000 → 5,500.000).
 */
export function formatNumber(
  n: number,
  locale: Locale = (i18next.language as Locale) ?? DEFAULT_LOCALE,
  options?: Intl.NumberFormatOptions,
): string {
  return new Intl.NumberFormat(intlLocale(locale), options).format(n);
}
